import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useSchedulesBreaksDestroyMutation } from '../../../../services/GLOWClient';
import EditBreak from './EditBreak';

const BreaksList = ({ breaks, onBreakUpdated, onBreakDeleted }) => {
  const [deleteBreak, { isLoading: isDeleting }] = useSchedulesBreaksDestroyMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBreak, setSelectedBreak] = useState(null);
  
  const handleEditClick = (breakItem) => {
    setSelectedBreak(breakItem);
    setEditDialogOpen(true);
  };
  
  const handleDeleteClick = (breakItem) => {
    setSelectedBreak(breakItem);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!selectedBreak) return;
    
    try {
      await deleteBreak({ breakId: selectedBreak.break_id }).unwrap();
      if (onBreakDeleted) {
        onBreakDeleted(selectedBreak.break_id);
      }
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete break:', error);
    }
  };
  
  const handleBreakUpdated = (updatedBreak) => {
    if (onBreakUpdated) {
      onBreakUpdated(updatedBreak);
    }
  };
  
  if (!breaks || breaks.length === 0) {
    return (
      <Box sx={{ mt: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Aucune pause définie
        </Typography>
      </Box>
    );
  }
  
  return (
    <>
      <List dense component={Paper} variant="outlined" sx={{ mt: 1, mb: 2 }}>
        {breaks.map((breakItem, index) => (
          <React.Fragment key={breakItem.break_id}>
            {index > 0 && <Divider />}
            
            <ListItem >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" component="span">
                      {breakItem.start_time} - {breakItem.end_time}
                    </Typography>
                    {breakItem.description && (
                      <Chip 
                        label={breakItem.description} 
                        size="small" 
                        color="info" 
                        variant="outlined"
                        sx={{ ml: 1, mr: 4, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Tooltip title="Modifier">
                  <IconButton 
                    edge="end" 
                    aria-label="edit" 
                    size="small"
                    onClick={() => handleEditClick(breakItem)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Supprimer">
                  <IconButton 
                    edge="end" 
                    aria-label="delete" 
                    size="small"
                    onClick={() => handleDeleteClick(breakItem)}
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          </React.Fragment>
        ))}
      </List>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer cette pause ?
            {selectedBreak && (
              <Box component="span" sx={{ fontWeight: 'bold', display: 'block', mt: 1 }}>
                {selectedBreak.start_time} - {selectedBreak.end_time}
                {selectedBreak.description && ` (${selectedBreak.description})`}
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Annuler
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained" 
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
          >
            {isDeleting ? 'Suppression...' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Break Dialog */}
      {selectedBreak && (
        <EditBreak
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={handleBreakUpdated}
          breakData={selectedBreak}
        />
      )}
    </>
  );
};

export default BreaksList;
